class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (event) => event.code === "Escape" && this.close()
    );

    this.querySelector("#CartDrawer-Overlay")?.addEventListener(
      "click",
      this.close.bind(this)
    );

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");

    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");

        if (
          containerToTrapFocusOn &&
          focusElement &&
          typeof trapFocus === "function"
        ) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");

    if (typeof removeTrapFocus === "function") {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling?.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details")?.hasAttribute("open")
      );
    });

    if (typeof onKeyUpEscape === "function") {
      cartDrawerNote.parentElement?.addEventListener("keyup", onKeyUpEscape);
    }
  }

  renderContents(parsedState) {
    const inner = this.querySelector(".drawer__inner");
    inner?.classList.remove("is-empty");

    this.productId = parsedState?.id;
    if (!parsedState?.sections) return;

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections[section.id];
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const replacementHtml = this.getSectionInnerHTML(
        sectionHtml,
        section.selector
      );

      if (sectionElement && replacementHtml !== null) {
        sectionElement.innerHTML = replacementHtml;
      }
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay")?.addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const element = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);

    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

function registerCartDrawerItems() {
  if (customElements.get("cart-drawer-items")) return true;

  const CartItemsElement = customElements.get("cart-items");
  if (!CartItemsElement) return false;

  class CartDrawerItems extends CartItemsElement {
    getSectionsToRender() {
      return [
        {
          id: "CartDrawer",
          section: "cart-drawer",
          selector: ".drawer__inner",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
      ];
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);
  return true;
}

if (!registerCartDrawerItems()) {
  customElements.whenDefined("cart-items").then(registerCartDrawerItems);
}

function injectJoffzCartFixStyles() {
  if (document.getElementById("joffz-cart-fix-styles")) return;

  const style = document.createElement("style");
  style.id = "joffz-cart-fix-styles";
  style.textContent = `
    .cart-drawer .cart-item__save-badge {
      display: none !important;
    }

    .cart-drawer .cart-item__discounted-prices {
      display: flex;
      align-items: baseline;
      flex-wrap: wrap;
      column-gap: 0.6rem;
    }

    .cart-drawer .cart-item__discounted-prices > dd:last-of-type {
      order: 1;
    }

    .cart-drawer .cart-item__discounted-prices > dd.price {
      order: 2;
      font-weight: 700;
    }

    .cart-drawer .cart-item__old-price {
      color: #c0392b !important;
      text-decoration: line-through !important;
      text-decoration-color: #c0392b !important;
      opacity: 0.85;
    }

    .price--on-sale .price__compare .price-item--regular {
      color: #c0392b !important;
      text-decoration-color: #c0392b !important;
      opacity: 0.85;
    }
  `;

  document.head.appendChild(style);
}

function normalizePath(path) {
  const normalized = path.replace(/\/{2,}/g, "/").replace(/\/$/, "");
  return normalized || "/";
}

function getLocaleRoot() {
  return window.Shopify?.routes?.root || "/";
}

function getCartPath() {
  return normalizePath(`${getLocaleRoot()}cart`);
}

function openCartFromQuery() {
  const params = new URLSearchParams(window.location.search);
  if (params.get("open_cart") !== "1") return;

  const drawer = document.querySelector("cart-drawer");
  if (drawer && typeof drawer.open === "function") {
    drawer.open(document.querySelector("#cart-icon-bubble"));
  }

  const cleanUrl = new URL(window.location.href);
  cleanUrl.searchParams.delete("open_cart");
  window.history.replaceState(
    {},
    "",
    `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`
  );
}

function redirectCartPageToDrawer() {
  if (window.Shopify?.designMode) return false;
  if (normalizePath(window.location.pathname) !== getCartPath()) return false;

  const target = new URL(window.location.href);
  target.pathname = getLocaleRoot();
  target.searchParams.set("open_cart", "1");
  window.location.replace(target.toString());
  return true;
}

function interceptCartPageLinks() {
  document.addEventListener("click", (event) => {
    const link = event.target.closest("a[href]");
    if (!link || link.closest("form")) return;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    if (normalizePath(url.pathname) !== getCartPath()) return;

    const drawer = document.querySelector("cart-drawer");
    if (!drawer || typeof drawer.open !== "function") return;

    event.preventDefault();
    drawer.open(link);
  });
}

injectJoffzCartFixStyles();
interceptCartPageLinks();

document.addEventListener("DOMContentLoaded", () => {
  if (!redirectCartPageToDrawer()) {
    openCartFromQuery();
  }
});
